import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button, Row, Col, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FieldArray, Formik, Form as FormikForm, ErrorMessage, FormikHelpers } from "formik";
import { CustomReactDropdownTreeSelect, TooltipCustom } from "@services/ui-components";
import { supplementsValidationSchema } from "./supplementsValidationSchema";
import TextInput from "@services/ui-components/src/TextInput/TextInput";
import MaxCountOnOrder from "./inputs/MaxCountOnOrder";
import PriceInput from "../../../MenuItemsPage/inputs/PriceInput";
import {
    DishForTree,
    LanguageVm,
    PartialDishSupplement,
    PartialMenuItem,
} from "../../../../../interfaces/meMenuInterfaces/interfaces";
import { buildNodeToDishIdsMap } from "../../utils/dishesInputServices/buildNodeToDishIdsMap";
import { prepareTreeData } from "../../utils/dishesInputServices/prepareTreeDataForDishesInput";
import { cloneNameToDisplay } from "../../../utils/cloneNameUtils";
import { TreeNode } from "react-dropdown-tree-select";
import { filterDishesTreeBySearch } from "../../utils/dishesInputServices/filterDishesTreeBySearch";
import {
    buildDefaultOptionsForCreate,
    buildOptionsFromExistingGroup,
    createEmptySupplementOption,
} from "../../utils/helpers";
import { buildTranslationTextVm } from "../../../utils/translationVersions/buildTranslationTextVm";
import { getCopyOriginProps } from "@me-team/common/src/components/Me-menu/utils/translationVersions/getCopyOriginProps";
import CopyOriginButton from "@me-team/common/src/components/Me-menu/components/CopyOriginButton/CopyOriginButton";
import { TranslationLockedFieldsInfoBanner } from "@me-team/common/src/components/Me-menu/components/TranslationLockedFieldsInfoBanner/TranslationLockedFieldsInfoBanner";
import TranslationMissingDot from "../../../components/LanguageVersionSelect/TranslationMissingDot";
import { CompanyAccess, MenuSupplementOptionInput } from "@me-team/host/main/graphql/types";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";
import ToastComponent from "@me-team/common/src/components/ui-components/ToastComponent/ToastComponent";
import { useToast } from "@me-team/common/src/hooks/useToast";

export interface SupplementsGroupFormValues {
    groupName: string;
    options: SupplementOptionFormValue[];
    isRequireOnOrder: boolean;
    maxCountOnOrder: number;
    items: number[];
}

export type SupplementsGroupSubmitValues = Omit<SupplementsGroupFormValues, "options"> & {
    options: MenuSupplementOptionInput[];
};

export interface SupplementOptionFormValue {
    uiId: string;
    id?: number;
    name: string;
    price: number | "";
}

interface SupplementsGroupFormModalProps {
    show: boolean;
    handleClose: () => void;
    onSave: (
        values: SupplementsGroupSubmitValues,
        formikHelpers: FormikHelpers<SupplementsGroupFormValues>
    ) => void | Promise<void>;
    dishesData: DishForTree[];
    existingGroup?: PartialDishSupplement;
    mode: "create" | "edit" | "restore";
    primaryLanguage: LanguageVm;
    selectedLanguage: LanguageVm;
    canCreateOptionsAndSupplements?: boolean;
    canEditOptionsAndSupplements?: boolean;
    canDeleteRestoreOptionsAndSupplements?: boolean;
}

const MAX_VISIBLE_DISH_TAGS = 5;
const NO_RESULTS_NODE_VALUE = "__no_results__";

const SupplementsGroupModal: React.FC<SupplementsGroupFormModalProps> = ({
    show,
    handleClose,
    onSave,
    dishesData,
    existingGroup,
    mode,
    primaryLanguage,
    selectedLanguage,
    canCreateOptionsAndSupplements,
    canEditOptionsAndSupplements,
    canDeleteRestoreOptionsAndSupplements,
}) => {
    const { t } = useTranslation();
    const translationTooltip = t("Edit the language version");
    const { showToast, toastText, setShowToast, toggleShowToast } = useToast();

    const isCreateMode = mode === "create";
    const isEditMode = mode === "edit";
    const isRestoreMode = mode === "restore";
    const isTranslationMode = !isCreateMode && primaryLanguage.code !== selectedLanguage.code;
    const headerLanguage = isCreateMode ? primaryLanguage : selectedLanguage;
    const canCreateOptionsAndSupplementsByRole = useRoleAccess(CompanyAccess.MenuOptionsAndSupplementsCreate);
    const canEditOptionsAndSupplementsByRole = useRoleAccess(CompanyAccess.MenuOptionsAndSupplementsEdit);
    const canDeleteRestoreOptionsAndSupplementsByRole = useRoleAccess(
        CompanyAccess.MenuOptionsAndSupplementsDeleteRestore
    );
    const canSubmitCreate = canCreateOptionsAndSupplements ?? canCreateOptionsAndSupplementsByRole;
    const canSubmitEdit = canEditOptionsAndSupplements ?? canEditOptionsAndSupplementsByRole;
    const canSubmitRestore = canDeleteRestoreOptionsAndSupplements ?? canDeleteRestoreOptionsAndSupplementsByRole;
    const canSubmit = isCreateMode ? canSubmitCreate : isEditMode ? canSubmitEdit : canSubmitRestore;
    const [dishesSearch, setDishesSearch] = useState("");
    const [isShowAllSelectedDishes, setIsShowAllSelectedDishes] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownWrapperRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const wrapper = dropdownWrapperRef.current;
        if (!wrapper) return;

        const trigger = wrapper.querySelector(".dropdown-trigger") as HTMLElement | null;
        if (!trigger) return;

        const updateState = () => {
            const expanded = trigger.getAttribute("aria-expanded") === "true";
            setIsDropdownOpen(expanded);
        };
        updateState();

        const observer = new MutationObserver(updateState);
        observer.observe(trigger, {
            attributes: true,
            attributeFilter: ["aria-expanded"],
        });

        return () => observer.disconnect();
    }, []);

    const dropdownDishToggle = useCallback(() => {
        const wrapper = dropdownWrapperRef.current;
        if (!wrapper) return;

        const trigger = wrapper.querySelector(".dropdown-trigger") as HTMLElement | null;
        if (!trigger) return;

        trigger.click();
    }, []);

    const title = isCreateMode
        ? t("New add-ons group")
        : isEditMode
          ? t("Edit add-ons group")
          : t("Restore add-ons group");

    const primaryButtonLabel = isCreateMode ? t("Save") : isRestoreMode ? t("Restore") : t("Save");

    const initialValues: SupplementsGroupFormValues = useMemo(() => {
        if (existingGroup) {
            const optionsFromExistingGroup = buildOptionsFromExistingGroup(existingGroup);

            const initialValuesForUpdate: SupplementsGroupFormValues = {
                groupName: existingGroup?.groupName ?? "",
                options: optionsFromExistingGroup,
                isRequireOnOrder: existingGroup?.isRequireOnOrder ?? false,
                maxCountOnOrder: existingGroup?.maxCountOnOrder ?? 1,
                items: existingGroup?.items?.map((item: PartialMenuItem) => item.id) ?? [],
            };

            return initialValuesForUpdate;
        }

        const defaultOptionsForCreate = buildDefaultOptionsForCreate();

        const initialValuesForCreate: SupplementsGroupFormValues = {
            groupName: "",
            items: [],
            options: defaultOptionsForCreate,
            isRequireOnOrder: false,
            maxCountOnOrder: 1,
        };

        return initialValuesForCreate;
    }, [existingGroup]);

    const nodeToDishIdsMap = useMemo(() => buildNodeToDishIdsMap(dishesData), [dishesData]);

    const getDishLabel = (dishId: number): React.ReactNode => {
        const dish = dishesData.find((d) => d.id === dishId);
        if (!dish) return String(dishId);

        const dishNameVm = buildTranslationTextVm({
            translatedValue: dish.name,
            originValue: dish.nameOrigin,
            isTranslationMode,
        });

        return (
            <span className="d-inline-flex align-items-center">
                <span className="me-1">{cloneNameToDisplay(dishNameVm.displayText, t)}</span>
                {dishNameVm.isMissingTranslation ? <TranslationMissingDot tooltipText={translationTooltip} /> : null}
            </span>
        );
    };

    return (
        <>
            <Modal show={show} onHide={handleClose}>
                <Modal.Header closeButton className="border-bottom-0">
                    <div className="d-flex flex-wrap gap-2">
                        <Modal.Title className="fs-5">{title}</Modal.Title>
                        <div className="d-flex justify-content-center align-items-center bg-violet-light rounded-1 p-1 px-6 ms-3">
                            <span className="fs-7 text-primary">{headerLanguage.label}</span>
                        </div>
                    </div>
                </Modal.Header>

            <Formik
                initialValues={initialValues}
                validationSchema={supplementsValidationSchema(t)}
                enableReinitialize
                onSubmit={async (values, formikHelpers) => {
                    if (!canSubmit) return;

                    const { setSubmitting } = formikHelpers;

                        try {
                            const optionsWithoutUiId = values.options.map(({ uiId, ...option }) => ({
                                ...(typeof option.id === "number" ? { id: option.id } : {}),
                                name: option.name,
                                price: Number(option.price),
                            }));
                            const valuesForSubmit: SupplementsGroupSubmitValues = {
                                ...values,
                                options: optionsWithoutUiId,
                            };
                            await onSave(valuesForSubmit, formikHelpers);
                        } finally {
                            setSubmitting(false);
                        }
                    }}
                >
                    {({ values, isSubmitting, setFieldValue, errors, touched }) => {
                        const treeData = prepareTreeData(dishesData, values.items, t);
                        const filteredTreeData = filterDishesTreeBySearch(treeData, dishesSearch);
                        const isSearchMode = dishesSearch.trim().length > 0;
                        const isTreeEmpty = treeData.length === 0;
                        const groupNameVm = buildTranslationTextVm({
                            translatedValue: values.groupName,
                            originValue: existingGroup?.groupNameOrigin,
                            isTranslationMode,
                        });

                        const emptyDropdownNode: TreeNode = {
                            label: t("Dishes not found"),
                            value: NO_RESULTS_NODE_VALUE,
                            disabled: true,
                            showCheckbox: false,
                            className: "no-results-node",
                        } as unknown as TreeNode;

                        let dropdownData: TreeNode[] = filteredTreeData;

                        if (isTreeEmpty || (isSearchMode && filteredTreeData.length === 0)) {
                            dropdownData = [emptyDropdownNode];
                        }

                        const handleTreeChange = (currentNode: TreeNode) => {
                            const nodeValue = String(currentNode.value);

                            if (nodeValue === NO_RESULTS_NODE_VALUE) {
                                return;
                            }

                            let leafIdsForNode = nodeToDishIdsMap[nodeValue] || [];

                            if (leafIdsForNode.length === 0) {
                                const numericId = Number(nodeValue);
                                if (!Number.isNaN(numericId)) {
                                    leafIdsForNode = [numericId];
                                } else {
                                    return;
                                }
                            }

                            const prevItems: number[] = values.items || [];
                            let newItems: number[];

                            if (currentNode.checked) {
                                const merged = new Set(prevItems);
                                leafIdsForNode.forEach((id) => merged.add(id));
                                newItems = Array.from(merged);
                            } else {
                                const toRemove = new Set(leafIdsForNode);
                                newItems = prevItems.filter((id) => !toRemove.has(id));
                            }
                            setFieldValue("items", newItems);
                        };

                        const removeDish = (dishId: number) => {
                            const newItems = values.items.filter((id) => id !== dishId);
                            setFieldValue("items", newItems);
                        };

                        const totalSelected = values.items.length;

                        const shouldCollapse = !isShowAllSelectedDishes && totalSelected > MAX_VISIBLE_DISH_TAGS;

                        const dishesToRender = shouldCollapse
                            ? values.items.slice(0, MAX_VISIBLE_DISH_TAGS)
                            : values.items;

                        const hiddenCount =
                            totalSelected > MAX_VISIBLE_DISH_TAGS ? totalSelected - MAX_VISIBLE_DISH_TAGS : 0;

                        return (
                            <FormikForm>
                                <Modal.Body>
                                    <div className="px-6">
                                        {isTranslationMode && <TranslationLockedFieldsInfoBanner />}
                                        <Form.Group controlId="groupName" className="mb-3 mb-lg-4">
                                            <Row>
                                                <Col sm={4}>
                                                    <label
                                                        htmlFor="supplementsModal-name-field"
                                                        className="fs-7 fw-bold text-start mb-sm-0 required-label"
                                                    >
                                                        {t("Additive group name")}
                                                    </label>
                                                </Col>
                                                <TextInput
                                                    id="supplementsModal-name-field"
                                                    inputCol={8}
                                                    name="groupName"
                                                    label=""
                                                    placeholder={
                                                        groupNameVm.placeholderText ?? "Add additive group name"
                                                    }
                                                    isLabel={false}
                                                    maxLength={50}
                                                    showCounter
                                                    rightSlot={
                                                        groupNameVm.isMissingTranslation ? (
                                                            <CopyOriginButton
                                                                {...getCopyOriginProps(groupNameVm)}
                                                                onCopied={() =>
                                                                    toggleShowToast(t("Original text copied"))
                                                                }
                                                            />
                                                        ) : null
                                                    }
                                                />
                                            </Row>
                                        </Form.Group>
                                        <Form.Group controlId="dishes" className="mb-3 mb-lg-4">
                                            <Row className="align-items-center">
                                                <Col sm={4}>
                                                    <label
                                                        htmlFor="supplementsModal-dishes-field"
                                                        className="fs-7 fw-bold text-start mb-sm-0"
                                                    >
                                                        {t("Dishes")}
                                                    </label>
                                                </Col>
                                                <Col sm={8}>
                                                    <div
                                                        className="position-relative dishes-select-wrapper"
                                                        ref={dropdownWrapperRef}
                                                    >
                                                        <CustomReactDropdownTreeSelect
                                                            id="supplementsModal-dishes-field"
                                                            name="items"
                                                            data={dropdownData}
                                                            texts={{ placeholder: t("Search dishes") }}
                                                            onChange={handleTreeChange}
                                                            showPartiallySelected={true}
                                                            keepOpenOnSelect={true}
                                                            className={"dishes-select-control"}
                                                            disabled={isTranslationMode}
                                                        />

                                                        <div
                                                            className={`dishes-select-input form-control ${touched?.items && errors?.items ? "is-invalid" : ""}  ${isTranslationMode ? "disabled" : ""}  rounded-1 d-flex flex-wrap align-items-center gap-2`}
                                                            style={{ minHeight: "49px" }}
                                                            onClick={(event) => {
                                                                event.preventDefault();
                                                                event.stopPropagation();
                                                                if (isTranslationMode) return;
                                                                dropdownDishToggle();
                                                            }}
                                                            aria-disabled={isTranslationMode}
                                                        >
                                                            <div className="dishes-select-input-inner d-flex flex-wrap align-items-center gap-2">
                                                                {dishesToRender.map((dishId) => (
                                                                    <span
                                                                        key={dishId}
                                                                        className="dish-tag fs-7 rounded-1 bg-violet-light text-primary d-inline-flex align-items-center px-2 py-1"
                                                                    >
                                                                        {getDishLabel(dishId)}
                                                                        <Button
                                                                            variant="link"
                                                                            type="button"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                e.preventDefault();
                                                                                removeDish(dishId);
                                                                            }}
                                                                            className="dish-tag-remove p-0 text-primary text-decoration-none ps-2"
                                                                            style={{ lineHeight: "1" }}
                                                                            disabled={isTranslationMode}
                                                                        >
                                                                            <i className={"bi bi-x-lg"} />
                                                                        </Button>
                                                                    </span>
                                                                ))}
                                                                {hiddenCount > 0 && !isShowAllSelectedDishes && (
                                                                    <button
                                                                        type="button"
                                                                        className="dish-tag-more fs-7 rounded-1 bg-violet-light text-primary d-inline-flex align-items-center px-2 py-1 border-0"
                                                                        onClick={(event) => {
                                                                            event.stopPropagation();
                                                                            event.preventDefault();
                                                                            setIsShowAllSelectedDishes(true);
                                                                        }}
                                                                    >
                                                                        {t("More")} {hiddenCount}
                                                                    </button>
                                                                )}
                                                                {hiddenCount > 0 && isShowAllSelectedDishes && (
                                                                    <button
                                                                        type="button"
                                                                        className="dish-tag-more fs-7 rounded-1 bg-violet-light text-primary d-inline-flex align-items-center px-2 py-1 border-0"
                                                                        onClick={(event) => {
                                                                            event.stopPropagation();
                                                                            event.preventDefault();
                                                                            setIsShowAllSelectedDishes(false);
                                                                        }}
                                                                    >
                                                                        {t("Hide")}
                                                                    </button>
                                                                )}
                                                                <input
                                                                    type="text"
                                                                    className="dishes-select-search-input"
                                                                    placeholder={
                                                                        totalSelected === 0
                                                                            ? t("Select a dish")
                                                                            : t("Search")
                                                                    }
                                                                    value={dishesSearch}
                                                                    onChange={(event) =>
                                                                        setDishesSearch(event.target.value)
                                                                    }
                                                                    onClick={(event) => {
                                                                        event.stopPropagation();
                                                                        if (isTranslationMode) return;
                                                                        dropdownDishToggle();
                                                                    }}
                                                                    disabled={isTranslationMode}
                                                                />
                                                                <i
                                                                    className={`bi bi-chevron-down dishes-select-arrow ${
                                                                        isDropdownOpen
                                                                            ? "dishes-select-arrow-rotate-up"
                                                                            : ""
                                                                    }`}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <ErrorMessage name="items">
                                                        {(msg) => <div className="invalid-feedback d-block">{msg}</div>}
                                                    </ErrorMessage>
                                                </Col>
                                            </Row>
                                        </Form.Group>
                                        <div className="mt-4">
                                            <h5 className="fs-16 mb-3">{t("List of additive/option variants")}</h5>
                                            <FieldArray
                                                name="options"
                                                render={(arrayHelpers) => (
                                                    <div>
                                                        {values.options.map((option, index) => {
                                                            const nameId = `supplementsModal-options.${index}.name-field`;
                                                            const priceId = `supplementsModal-options.${index}.price-field`;
                                                            const optionNameVm = buildTranslationTextVm({
                                                                translatedValue: option.name,
                                                                originValue:
                                                                    existingGroup?.options?.[index]?.nameOrigin ?? null,
                                                                isTranslationMode,
                                                            });

                                                            return (
                                                                <div key={option.uiId} className="mb-3">
                                                                    <Row className="mb-3">
                                                                        <Col sm={4}>
                                                                            <label
                                                                                htmlFor={nameId}
                                                                                className="fs-7 fw-bold text-start mb-sm-0 required-label"
                                                                            >
                                                                                {t("Additive/ option name")}
                                                                            </label>
                                                                        </Col>
                                                                        <Col sm={8}>
                                                                            <Form.Group controlId={nameId}>
                                                                                <TextInput
                                                                                    name={`options.${index}.name`}
                                                                                    id={nameId}
                                                                                    inputCol={12}
                                                                                    placeholder={
                                                                                        optionNameVm.placeholderText ??
                                                                                        "Add-on/option name"
                                                                                    }
                                                                                    isLabel={false}
                                                                                    label=""
                                                                                    maxLength={50}
                                                                                    showCounter
                                                                                    rightSlot={
                                                                                        optionNameVm.isMissingTranslation ? (
                                                                                            <CopyOriginButton
                                                                                                {...getCopyOriginProps(
                                                                                                    optionNameVm
                                                                                                )}
                                                                                                onCopied={() =>
                                                                                                    toggleShowToast(
                                                                                                        t(
                                                                                                            "Original text copied"
                                                                                                        )
                                                                                                    )
                                                                                                }
                                                                                            />
                                                                                        ) : null
                                                                                    }
                                                                                />
                                                                            </Form.Group>
                                                                        </Col>
                                                                    </Row>
                                                                    <Row className="align-items-center mb-3">
                                                                        <Col sm={4}>
                                                                            <label
                                                                                htmlFor={priceId}
                                                                                className="fs-7 fw-bold text-start mb-sm-0 required-label"
                                                                            >
                                                                                {t("Option price")}
                                                                            </label>
                                                                        </Col>
                                                                        <Col sm={8} className="d-flex">
                                                                            <Form.Group
                                                                                controlId={priceId}
                                                                                className="w-100"
                                                                            >
                                                                                <PriceInput
                                                                                    name={`options.${index}.price`}
                                                                                    placeholder="0.00"
                                                                                    id={priceId}
                                                                                    initialValue={
                                                                                        option.price === ""
                                                                                            ? 0
                                                                                            : Number(option.price)
                                                                                    }
                                                                                    disabled={isTranslationMode}
                                                                                />
                                                                            </Form.Group>

                                                                        {values.options.length > 1 && (
                                                                            <div className="d-flex align-self-start ms-2">
                                                                                <TooltipCustom
                                                                                    placement="bottom"
                                                                                    text={t("Delete add-on/option")}
                                                                                >
                                                                                    <Button
                                                                                        variant="outline-secondary"
                                                                                        className="w-100 h-100 px-3 py-6 fs-normal rounded-1"
                                                                                        onClick={() => {
                                                                                            const nextOptionsLength =
                                                                                                values.options.length - 1;
                                                                                            arrayHelpers.remove(index);
                                                                                            if (
                                                                                                values.maxCountOnOrder >
                                                                                                nextOptionsLength
                                                                                            ) {
                                                                                                setFieldValue(
                                                                                                    "maxCountOnOrder",
                                                                                                    nextOptionsLength
                                                                                                );
                                                                                            }
                                                                                        }}
                                                                                        disabled={isTranslationMode}
                                                                                    >
                                                                                        <i className="bi bi-trash3 text-dark" />
                                                                                    </Button>
                                                                                </TooltipCustom>
                                                                            </div>
                                                                        )}
                                                                    </Col>
                                                                </Row>
                                                            </div>
                                                        );
                                                    })}
                                                    <Col sm={8} className="ms-auto ps-2">
                                                        <Button
                                                            type="button"
                                                            variant="light"
                                                            className="fs-7 text-primary p-0 border-0 bg-transparent"
                                                            onClick={() =>
                                                                arrayHelpers.push(createEmptySupplementOption())
                                                            }
                                                            disabled={isTranslationMode}
                                                        >
                                                            <i className="bi bi-plus me-1" />
                                                            {t("Add another additive variant or option")}
                                                        </Button>
                                                    </Col>
                                                </div>
                                            )}
                                        />
                                    </div>
                                    <Form.Group controlId="isRequireOnOrder" className="mt-4">
                                        <Row className="align-items-center">
                                            <Col sm={4}>
                                                <label
                                                    htmlFor="supplementsModal-isRequireOnOrder-field"
                                                    className="fs-7 fw-bold text-start mb-sm-0"
                                                >
                                                    {t("Selection when ordering")}
                                                </label>
                                            </Col>
                                            <Col sm={8}>
                                                <Form.Check
                                                    id="supplementsModal-isRequireOnOrder-field"
                                                    name="isRequireOnOrder"
                                                    type="switch"
                                                    label={values.isRequireOnOrder ? t("Mandatory") : t("Optional")}
                                                    checked={values.isRequireOnOrder}
                                                    onChange={(e) =>
                                                        setFieldValue("isRequireOnOrder", e.target.checked)
                                                    }
                                                    disabled={isTranslationMode}
                                                />
                                            </Col>
                                        </Row>
                                    </Form.Group>
                                    <Form.Group controlId="maxCountOnOrder" className="mt-3">
                                        <Row>
                                            <Col sm={4}>
                                                <label
                                                    htmlFor="supplementsModal-maxCountOnOrder-field"
                                                    className="fs-7 fw-bold text-start mb-sm-0 required-label"
                                                >
                                                    {t("Max selection quantity")}
                                                </label>
                                            </Col>
                                            <Col sm={8}>
                                                <MaxCountOnOrder disabled={isTranslationMode} />
                                                <ErrorMessage name="maxCountOnOrder">
                                                    {(msg) => <div className="invalid-feedback d-block">{msg}</div>}
                                                </ErrorMessage>
                                            </Col>
                                        </Row>
                                    </Form.Group>
                                </div>
                            </Modal.Body>
                            <Modal.Footer className="justify-content-between border-top-0 flex-nowrap">
                                <Button
                                    variant="outline-primary"
                                    className="custom-btn"
                                    type="button"
                                    onClick={handleClose}
                                    disabled={isSubmitting}
                                >
                                    {t("cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    variant="primary"
                                    className="custom-btn fw-bold"
                                    disabled={isSubmitting || !canSubmit}
                                >
                                    {primaryButtonLabel}
                                </Button>
                            </Modal.Footer>
                        </FormikForm>
                    );
                }}
            </Formik>
        </Modal>
        {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={toastText} />}
        </>
    );
};

export default SupplementsGroupModal;
